/* # Announcements Page
   - Displays property announcements
   - Integrated with react-i18next for multi-language support (English/Greek)
   - Handles creation, editing, and deletion for authorized users
*/
import { useState, useEffect } from 'react';
import { LuMegaphone, LuPlus, LuTrash2, LuPencil, LuX } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useBuilding } from '../contexts/BuildingContext';
import { canManageAnnouncements } from '../utils/roleCheck';
import { createAnnouncementNotifications } from '../utils/notificationHelpers';
import { toast } from '../hooks/use-toast';

export default function Announcements() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { currentBuilding, currentProperty } = useBuilding();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ title: '', body: '', scope: 'PROPERTY', building_id: null });

  const canManage = canManageAnnouncements(user?.role);

  useEffect(() => {
    loadAnnouncements();
  }, [currentProperty, currentBuilding]);

  const loadAnnouncements = async () => {
    try {
      setLoading(true);
      const filters = [];
      if (currentProperty?.id) {
        filters.push({ name: 'property_id', op: 'Equal', value: currentProperty.id });
      }
      const { data, error } = await window.ezsite.apis.tablePage(70656, {
        PageNo: 1,
        PageSize: 100,
        OrderByField: 'created_at',
        IsAsc: false,
        Filters: filters
      });
      if (error) throw error;
      setAnnouncements(data?.List || []);
    } catch (error) {
      console.error('Failed to load announcements:', error);
      toast({
        title: t('common.error'),
        description: t('announcements.loadFailed'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canManage) return;

    try {
      const payload = {
        ...formData,
        property_id: currentProperty?.id,
        building_id: formData.scope === 'BUILDING' ? formData.building_id : null,
        created_by: user.id,
        created_at: new Date().toISOString()
      };

      if (editingId) {
        await window.ezsite.apis.tableUpdate(70656, { id: editingId, ...payload });
        toast({ title: t('common.success'), description: t('announcements.updated') });
      } else {
        await window.ezsite.apis.tableCreate(70656, payload);
        toast({ title: t('common.success'), description: t('announcements.published') });
        // Notify users
        createAnnouncementNotifications(payload, user.name).catch((e) =>
        console.error('Failed to send announcement notifications:', e)
        );
      }
      setShowModal(false);
      setEditingId(null);
      loadAnnouncements();
    } catch (error) {
      console.error('Failed to save announcement:', error);
      toast({
        title: t('common.error'),
        description: t('announcements.saveFailed'),
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (id) => {
    if (!confirm(t('announcements.deleteConfirm'))) return;
    try {
      await window.ezsite.apis.tableDelete(70656, { ID: id });
      toast({ title: t('common.success'), description: t('announcements.deleted') });
      loadAnnouncements();
    } catch (error) {
      console.error('Failed to delete announcement:', error);
      toast({
        title: t('common.error'),
        description: t('announcements.deleteFailed'),
        variant: 'destructive'
      });
    }
  };

  if (loading) return <div className="flex justify-center p-12 text-blue-600">{t('common.loading')}</div>;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-50 rounded-lg"><LuMegaphone className="w-6 h-6 text-orange-600" /></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('announcements.title')}</h1>
            <p className="text-sm text-gray-600">{t('announcements.subtitle')}</p>
          </div>
        </div>
        {canManage &&
        <button
          onClick={() => {setEditingId(null);setShowModal(true);}}
          className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition">

            <LuPlus className="w-4 h-4" /> {t('announcements.newAnnouncement')}
          </button>
        }
      </div>

      {announcements.length === 0 ?
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400">
          {t('announcements.noAnnouncements')}
        </div> :

      <div className="space-y-4">
          {announcements.map((ann) =>
        <div key={ann.id} className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-bold text-gray-900">{ann.title}</h3>
                    {ann.scope === 'PROPERTY' &&
                <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-[10px] font-bold uppercase rounded">
                        {t('faultReports.propertyWide')}
                      </span>
                }
                  </div>
                  <p className="text-gray-600 whitespace-pre-wrap leading-relaxed">{ann.body}</p>
                  <p className="text-[10px] text-gray-400 font-bold uppercase mt-4">
                    {t('announcements.posted', { date: new Date(ann.created_at).toLocaleDateString() })}
                  </p>
                </div>
                {canManage &&
            <div className="flex gap-2 ml-4">
                    <button
                onClick={() => {
                  setEditingId(ann.id);
                  setFormData({ title: ann.title, body: ann.body, scope: ann.scope });
                  setShowModal(true);
                }}
                className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg">

                      <LuPencil className="w-4 h-4" />
                    </button>
                    <button
                onClick={() => handleDelete(ann.id)}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg">

                      <LuTrash2 className="w-4 h-4" />
                    </button>
                  </div>
            }
              </div>
            </div>
        )}
        </div>
      }

      {showModal &&
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-8 shadow-2xl animate-in zoom-in duration-200">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">
                {editingId ? t('common.edit') : t('announcements.publish')} {t('announcements.title').toLowerCase()}
              </h2>
              <button
              onClick={() => setShowModal(false)}
              className="p-2 hover:bg-gray-100 rounded-lg">

                <LuX className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">{t('announcements.announcementTitle')}</label>
                <input
                required
                className="w-full border rounded-xl p-3 bg-gray-50 focus:bg-white outline-none"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })} />

              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">{t('announcements.message')}</label>
                <textarea
                required
                rows={6}
                className="w-full border rounded-xl p-3 bg-gray-50 focus:bg-white outline-none"
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })} />

              </div>
              <div className="flex gap-3 pt-4">
                <button
                type="button"
                onClick={() => setShowModal(false)}
                className="flex-1 py-3 border rounded-xl font-bold">

                  {t('common.cancel')}
                </button>
                <button
                type="submit"
                className="flex-1 py-3 bg-orange-600 text-white rounded-xl font-bold shadow-lg shadow-orange-100">

                  {editingId ? t('common.update') : t('announcements.publish')}
                </button>
              </div>
            </form>
          </div>
        </div>
      }
    </div>);

}